<script lang="ts" setup>
import BaseDialog from '@/components/dialogs/BaseDialog.vue'
import ResponsiveImage from '@/components/ResponsiveImage.vue'
import SkrCheckbox from '@/components/SkrCheckbox.vue'

const props = withDefaults(
  defineProps<{
    loading?: boolean
    terms?: Set<Term>
    policies?: Set<Policy>
    context: TermsContext
  }>(),
  {
    loading: false,
    terms: () => new Set(),
    policies: () => new Set(),
  }
)

const emit = defineEmits<{
  (event: 'action' | 'close'): void
}>()

const { t } = useI18n()
const { track } = useAnalytics()

onMounted(() => {
  track(ANALYTICS_EVENTS.TC_MODAL_VIEWED, { context: props.context })
})

const onDismiss = () => {
  track(ANALYTICS_EVENTS.TC_MODAL_DISMISSED, { context: props.context })
  emit('close')
}

const data = reactive({
  skribbleAccepted: false,
  swisscomAccepted: false,
})

const allConsentGiven = computed((): boolean => {
  return (
    (!props.terms.has('skribble') || data.skribbleAccepted) && (!props.terms.has('swisscom') || data.swisscomAccepted)
  )
})

const isNas = computed(() => props.terms.has('skribble'))

const title = computed(() => {
  return isNas.value ? t('viewer.legal.overlay.title_nas') : t('viewer.legal.overlay.title')
})

const onlySkribblePolicy = computed(() => props.policies.has('skribble') && props.policies.size === 1)
const combinedPolicy = computed(
  () => props.policies.has('skribble') && props.policies.has('pxl_vision') && props.policies.has('swisscom')
)
</script>

<template>
  <base-dialog
    data-cy="termsOfUseDialog"
    max-width="640"
    :title="title"
    :disabled="!allConsentGiven"
    :loading="loading"
    :action-text="t('viewer.legal.overlay.accept')"
    :cancel-text="t('viewer.legal.overlay.decline')"
    @action="emit('action')"
    @close="onDismiss"
  >
    <v-row>
      <v-col cols="12" sm="7">
        <p v-if="!isNas">
          {{ t('viewer.legal.overlay.text') }}
        </p>
        <div class="my-6 space-y-4">
          <template v-if="props.terms.has('skribble')">
            <skr-checkbox v-model="data.skribbleAccepted" data-cy="skribble_checkbox">
              <i18n-t
                keypath="viewer.legal.overlay.skribble.accept_new.label_text"
                tag="div"
                for="viewer.legal.overlay.skribble.accept_new.label_link_text"
              >
                <a
                  :href="t('viewer.legal.overlay.skribble.accept_new.label_link_url')"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                  >{{ t('viewer.legal.overlay.skribble.accept_new.label_link_text') }}</a
                >
              </i18n-t>
            </skr-checkbox>
          </template>
          <template v-if="props.terms.has('swisscom')">
            <skr-checkbox v-model="data.swisscomAccepted" data-cy="swisscom_checkbox">
              <i18n-t
                keypath="viewer.legal.overlay.swisscom.accept_new.label_text"
                tag="div"
                for="viewer.legal.overlay.swisscom.accept_new.label_link_text"
              >
                <a
                  :href="t('viewer.legal.overlay.swisscom.accept_new.label_link_url')"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                  >{{ t('viewer.legal.overlay.swisscom.accept_new.label_link_text') }}</a
                >
              </i18n-t>
            </skr-checkbox>
          </template>
        </div>
        <small v-if="onlySkribblePolicy">
          <i18n-t keypath="dialog.accept_terms.privacy_policy.text">
            <template #link>
              <a :href="t('global.legal.privacy_policy_link')" target="_blank" rel="noopener">{{
                t('dialog.accept_terms.privacy_policy.link_text')
              }}</a>
            </template>
          </i18n-t>
        </small>
        <small v-else-if="combinedPolicy">
          <i18n-t keypath="viewer.legal.overlay.privacy_policy.label_text" tag="div" :plural="3">
            <a
              :href="t('viewer.legal.overlay.privacy_policy.skribble.label_link_url')"
              target="_blank"
              rel="noopener"
              >{{ t('viewer.legal.overlay.privacy_policy.skribble.label_link_text') }}</a
            >
            <a
              :href="t('viewer.legal.overlay.privacy_policy.pxl_vision.label_link_url')"
              target="_blank"
              rel="noopener"
              >{{ t('viewer.legal.overlay.privacy_policy.pxl_vision.label_link_text') }}</a
            >
            <a
              :href="t('viewer.legal.overlay.privacy_policy.swisscom.label_link_url')"
              target="_blank"
              rel="noopener"
              >{{ t('viewer.legal.overlay.privacy_policy.swisscom.label_link_text') }}</a
            >
          </i18n-t>
        </small>
      </v-col>
      <v-col v-if="$vuetify.display.smAndUp" order="1" sm="5">
        <responsive-image source="gtc-dialog" :width="142" :height="225" />
      </v-col>
    </v-row>
  </base-dialog>
</template>
